import {
  CheckCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  CopyOutlined,
  LinkOutlined,
  QrcodeOutlined,
  FormOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  FireOutlined,
  } from "@ant-design/icons";
import {
    Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
  Image,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { rewardAPIWithErrorHandling } from "../../../api/reward/rewardApi";
import { useNavigate } from "react-router-dom";
import { resolveImageUrl } from "../../../utils/image";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { semantic, brand, neutral } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;

const isActive = (status) => status === "1" || status === true || status === 1;

/* ─── mini stat card ─────────────────────────────────────────────── */
function StatCard({ icon, label, value, accentColor }) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 16px",
            background: "#fff",
            borderRadius: 8,
            border: "1px solid #eaeef5",
            minWidth: 110,
        }}>
            <div style={{
                width: 34, height: 34,
                borderRadius: 8,
                background: accentColor + "18",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
            }}>
                <span style={{ color: accentColor, fontSize: 16 }}>{icon}</span>
            </div>
            <div>
                <div style={{ fontSize: 11, color: "#999", lineHeight: 1.2, whiteSpace: "nowrap" }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: brand.primary, lineHeight: 1.3 }}>{value}</div>
            </div>
        </div>
    );
}

/* ─── access pills ───────────────────────────────────────────────── */
function AccessCell({ record, REACT_S3_IMAGE, REACT_APP_REWARD_QR, onCopy }) {
    const webUrl = record.shorturl || `${REACT_APP_REWARD_QR}/khao-sat/${record.slug}`;
    const appUrl = record.miniapp_id
        ? `https://zalo.me/s/${record.miniapp_id}/?pathRedirect=/program-register/${record.id}`
        : null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Web */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                    fontSize: 10, fontWeight: 600, color: brand.accent,
                    background: semantic.infoSubtle, borderRadius: 4,
                    padding: "1px 7px", letterSpacing: 0.3,
                }}>WEB</span>
                {record.qrcode && (
                    <Image
                        width={24} height={24}
                        src={resolveImageUrl(record.qrcode)}
                        preview={{ mask: <EyeOutlined style={{ fontSize: 8 }} /> }}
                        style={{ borderRadius: 3, border: "1px solid #e0e0e0", objectFit: "cover" }}
                    />
                )}
                <Tooltip title={record.shorturl ? "Copy link rút gọn" : "Copy link gốc"}>
                    <button
                        className="rdg-copy-btn"
                        onClick={() => onCopy(record.shorturl || webUrl)}
                    >
                        <CopyOutlined />
                    </button>
                </Tooltip>
                <Tooltip title="Copy link gốc">
                    <button
                        className="rdg-copy-btn"
                        onClick={() => onCopy(`${REACT_APP_REWARD_QR}/khao-sat/${record.slug}`)}
                    >
                        <LinkOutlined />
                    </button>
                </Tooltip>
            </div>

            {/* MiniApp */}
            {appUrl && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                        fontSize: 10, fontWeight: 600, color: semantic.success,
                        background: semantic.successSubtle, borderRadius: 4,
                        padding: "1px 7px", letterSpacing: 0.3,
                    }}>APP</span>
                    {record.miniapp_link && (
                        <Image
                            width={24} height={24}
                            src={resolveImageUrl(record.miniapp_link)}
                            preview={{ mask: <EyeOutlined style={{ fontSize: 8 }} /> }}
                            style={{ borderRadius: 3, border: "1px solid #e0e0e0", objectFit: "cover" }}
                        />
                    )}
                    <Tooltip title="Copy link MiniApp">
                        <button className="rdg-copy-btn" onClick={() => onCopy(appUrl)}>
                            <CopyOutlined />
                        </button>
                    </Tooltip>
                </div>
            )}
        </div>
    );
}

/* ─── main component ─────────────────────────────────────────────── */
export default function RewardDataGwTable() {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const REACT_S3_IMAGE      = process.env.REACT_S3_IMAGE      || "https://minio.conek.vn/loyalty";
    const REACT_APP_REWARD_QR = process.env.REACT_APP_REWARD_QR || "https://loyaltysmart.conek.vn";

    const [loading, setLoading]       = useState(false);
    const [data, setData]             = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const loadData = async (params = {}) => {
        try {
            setLoading(true);
            const res = await rewardAPIWithErrorHandling.getList({
                page:     pagination.current,
                per_page: pagination.pageSize,
                ...params,
            });
            const rd = res.data;
            if (rd.success) {
                setData(rd?.data?.data || []);
                setPagination(prev => ({
                    ...prev,
                    current: rd?.data?.current_page,
                    total:   rd?.data?.total,
                }));
            }
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData({}); }, []);

    const refresh = () => loadData({
        page:     pagination.current,
        per_page: pagination.pageSize,
        search:   form.getFieldValue("search"),
    });

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        loadData({ page: 1, search: form.getFieldValue("search") });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        loadData({ page: 1, search: "" });
    };

    const handleTableChange = (pg) => {
        setPagination(pg);
        loadData({ page: pg.current, per_page: pg.pageSize });
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        message.success("Đã sao chép!");
    };

    /* actions */
    const handleAdd               = ()       => navigate("/store-survey");
    const handleEdit              = (r)      => navigate(`/survey/${r.id}`);
    const handleViewSurveyDetail  = (r)      => navigate(`/survey-responses/${r.id}`);
    const handleViewPublic        = (r)      => window.open(`/khao-sat/${r.slug}`, "_blank");

    const handleDelete = async (record) => {
        try {
            await rewardAPIWithErrorHandling.delete(record.id);
            message.success("Xóa chương trình thành công");
            refresh();
        } catch (err) {
            message.error(err.message || "Không thể xóa chương trình");
        }
    };

    const handleToggle = async (record) => {
        try {
            await rewardAPIWithErrorHandling.toggle(record.id);
            message.success("Cập nhật trạng thái thành công");
            refresh();
        } catch (err) {
            message.error(err.message || "Không thể cập nhật trạng thái");
        }
    };

    /* derived stats */
    const totalSurvey = data.filter(d => d.form_type === "survey").length;
    const totalReward = data.filter(d => d.form_type !== "survey").length;
    const totalActive = data.filter(d => isActive(d.status)).length;

    /* ── columns ── */
    const columns = [
        {
            title: "#",
            key: "index",
            width: 48,
            fixed: "left",
            align: "center",
            render: (_, __, i) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {(pagination.current - 1) * pagination.pageSize + i + 1}
                </Text>
            ),
        },
        {
            title: "Chương trình",
            dataIndex: "title",
            key: "title",
            width: 240,
            render: (text, record) => {
                const isS = record.form_type === "survey";
                return (
                    <div>
                        <div
                            style={{
                                fontWeight: 600,
                                color: brand.primary,
                                fontSize: 13,
                                lineHeight: 1.4,
                                cursor: "pointer",
                                marginBottom: 4,
                            }}
                            onClick={() => handleViewSurveyDetail(record)}
                            className="rdg-title-link"
                        >
                            {text || "—"}
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <StatusTag style={{ fontSize: 10, padding: "0 6px", lineHeight: "18px" }}>
                                {isS ? "Khảo sát" : "Phần thưởng"}
                            </StatusTag>
                            {record.promotion_type && (
                                <StatusTag style={{ fontSize: 10, padding: "0 6px", lineHeight: "18px" }}>
                                    {record.promotion_type === "DIENTHOAI" ? "Thẻ ĐT"
                                     : record.promotion_type === "DATA"    ? "Data"
                                     : record.promotion_type === "GAME"    ? "Thẻ Game"
                                     : record.promotion_type}
                                </StatusTag>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Trạng thái",
            key: "status",
            width: 100,
            align: "center",
            render: (_, record) => {
                const on = isActive(record.status);
                return (
                    <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 12, fontWeight: 500,
                        color: on ? "#389e0d" : "#999",
                        background: on ? "#f6ffed" : "#fafafa",
                        border: `1px solid ${on ? "#b7eb8f" : "#e8e8e8"}`,
                        borderRadius: 20, padding: "2px 10px",
                    }}>
                        <span style={{
                            width: 6, height: 6, borderRadius: "50%",
                            background: on ? "#52c41a" : "#ccc",
                            display: "inline-block",
                        }} />
                        {on ? "Đang bật" : "Tắt"}
                    </span>
                );
            },
        },
        {
            title: "Kết quả",
            dataIndex: "submissions_count",
            key: "submissions_count",
            width: 90,
            align: "center",
            render: (val) => (
                <div style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 36, height: 24,
                    background: val > 0 ? "#e6f4ff" : "#fafafa",
                    color:      val > 0 ? "#1677ff" : "#aaa",
                    border:     `1px solid ${val > 0 ? "#91caff" : "#e8e8e8"}`,
                    borderRadius: 12,
                    fontSize: 12, fontWeight: 600, padding: "0 10px",
                }}>
                    {val ?? 0}
                </div>
            ),
        },
        {
            title: "Hạn lượng",
            dataIndex: "limit",
            key: "limit",
            width: 110,
            align: "center",
            render: (limit) => (
                <span style={{
                    fontSize: 12, fontWeight: 500,
                    color:      limit > 0 ? "#389e0d" : "#cf1322",
                    background: limit > 0 ? "#f6ffed"  : "#fff1f0",
                    border:     `1px solid ${limit > 0 ? "#b7eb8f" : "#ffa39e"}`,
                    borderRadius: 12, padding: "2px 10px",
                    display: "inline-block",
                }}>
                    {limit > 0 ? `${limit} lượt` : "Hết lượt"}
                </span>
            ),
        },
        {
            title: "Ngày tạo",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            align: "center",
            render: (text) => (
                <div style={{ lineHeight: 1.5 }}>
                    <div style={{ fontSize: 12, color: brand.primary }}>
                        {dayjs(text).format("DD/MM/YYYY")}
                    </div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>
                        {dayjs(text).format("HH:mm")}
                    </div>
                </div>
            ),
        },
        {
            title: "Tiếp cận",
            key: "access",
            width: 160,
            render: (_, record) => (
                <AccessCell
                    record={record}
                    REACT_S3_IMAGE={REACT_S3_IMAGE}
                    REACT_APP_REWARD_QR={REACT_APP_REWARD_QR}
                    onCopy={copyToClipboard}
                />
            ),
        },
        {
            title: "Thao tác",
            key: "operation",
            align: "center",
            fixed: "right",
            width: 170,
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title={isActive(record.status) ? "Tắt" : "Kích hoạt"}>
                        <Button
                            size="small"
                            icon={isActive(record.status) ? <CheckCircleOutlined /> : <StopOutlined />}
                            style={{
                                color:       isActive(record.status) ? semantic.success : semantic.danger,
                                borderColor: isActive(record.status) ? semantic.success : semantic.danger,
                            }}
                            onClick={() => handleToggle(record)}
                        />
                    </Tooltip>

                    <RowActions
                        onView={() => handleViewSurveyDetail(record)}
                        viewTooltip="Xem kết quả"
                        items={[
                            {
                                key: "public",
                                icon: <SearchOutlined />,
                                label: "Xem phiếu công khai",
                                onClick: () => handleViewPublic(record),
                            },
                            {
                                key: "edit",
                                icon: ACTION_ICON.edit,
                                label: "Chỉnh sửa",
                                onClick: () => handleEdit(record),
                            },
                            {
                                key: "delete",
                                icon: ACTION_ICON.delete,
                                label: "Xóa chương trình",
                                danger: true,
                                divider: true,
                                onClick: () =>
                                    confirmDelete(
                                        record.title || `chương trình #${record.id}`,
                                        () => handleDelete(record),
                                        "Chương trình sẽ bị xóa vĩnh viễn."
                                    ),
                            },
                        ]}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "0 0 32px", background: "#f4f6fb", minHeight: "100vh" }}>

            {/* ── Page Header ── */}
            <div style={{
                background: "#fff",
                borderBottom: "1px solid #eaeef5",
                padding: "14px 24px 12px",
            }}>
                <Breadcrumb
                    separator="›"
                    style={{ marginBottom: 10, fontSize: 12 }}
                    items={[
                        {
                            href: "/",
                            title: <><HomeOutlined /> Trang chủ</>,
                        },
                        {
                            title: "Khảo sát & Quà tặng",
                        },
                    ]}
                />

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: brand.primary, lineHeight: 1.3 }}>
                            Phiếu khảo sát & Quà tặng
                        </h2>
                        <p style={{ margin: "2px 0 0", fontSize: 13, color: neutral[500] }}>
                            Quản lý chương trình thu thập phản hồi và phân phối phần thưởng
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <StatCard
                            icon={<ThunderboltOutlined />}
                            label="Tổng"
                            value={pagination.total}
                            accentColor="#1677ff"
                        />
                        <StatCard
                            icon={<FormOutlined />}
                            label="Khảo sát"
                            value={totalSurvey}
                            accentColor="#722ed1"
                        />
                        <StatCard
                            icon={<GiftOutlined />}
                            label="Phần thưởng"
                            value={totalReward}
                            accentColor="#13c2c2"
                        />
                        <StatCard
                            icon={<FireOutlined />}
                            label="Đang bật"
                            value={totalActive}
                            accentColor="#52c41a"
                        />
                    </div>
                </div>
            </div>

            {/* ── Content area ── */}
            <div style={{ padding: "20px 24px 0" }}>

                {/* Filter bar */}
                <Card
                    style={{ borderRadius: 10, marginBottom: 14, border: "1px solid #eaeef5" }}
                    bodyStyle={{ padding: "12px 16px" }}
                >
                    <Form form={form} onFinish={handleSearch}>
                        <Row gutter={10} align="middle">
                            <Col flex="1 1 300px">
                                <Form.Item name="search" style={{ margin: 0 }}>
                                    <Input
                                        prefix={<SearchOutlined style={{ color: "#c0c4cc" }} />}
                                        placeholder="Tìm theo tên chương trình..."
                                        allowClear
                                        onPressEnter={handleSearch}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col>
                                <Space size={8}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SearchOutlined />}
                                        style={{ borderRadius: 8 }}
                                    >
                                        Tìm kiếm
                                    </Button>
                                    <Tooltip title="Làm mới bộ lọc">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={handleReset}
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Tooltip>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAdd}
                                        style={{
                                            borderRadius: 8,
                                            background: semantic.success,
                                            borderColor: semantic.success,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Thêm mới
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* Table */}
                <Card
                    style={{ borderRadius: 10, border: "1px solid #eaeef5", overflow: "hidden" }}
                    bodyStyle={{ padding: 0 }}
                >
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ["10", "15", "20", "50"],
                            showTotal: (total, range) =>
                                `${range[0]}–${range[1]} / ${total} mục`,
                            style: { padding: "12px 16px", margin: 0 },
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content", y: "calc(100vh - 390px)" }}
                        size="small"
                        rowClassName={(_, i) => i % 2 === 0 ? "rdg-row-even" : "rdg-row-odd"}
                    />
                </Card>
            </div>

            {/* ── scoped styles ── */}
            <style>{`
                /* table header */
                .rdg-row-even td, .rdg-row-odd td { vertical-align: middle !important; }
                .rdg-row-even { background: #fff; }
                .rdg-row-odd  { background: #fafbfd; }
                .ant-table-tbody > tr:hover > td { background: #eef5ff !important; transition: background 0.15s; }
                .ant-table-thead > tr > th {
                    background: #f0f4fb !important;
                    font-weight: 700 !important;
                    font-size: 12px !important;
                    color: #4a5568 !important;
                    border-bottom: 1.5px solid #e2e8f0 !important;
                    padding: 10px 12px !important;
                }
                .ant-table-cell { padding: 8px 12px !important; }

                /* copy button */
                .rdg-copy-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 22px; height: 22px; border-radius: 5px;
                    background: #f0f0f0; border: none; cursor: pointer;
                    color: #666; font-size: 11px;
                    transition: background 0.15s, color 0.15s;
                }
                .rdg-copy-btn:hover { background: #1677ff; color: #fff; }

                /* title link */
                .rdg-title-link:hover { color: #1677ff !important; }

                /* stat card label/value color override */
                .rdg-stat-label { font-size: 11px; color: rgba(255,255,255,0.7); }
                .rdg-stat-value { font-size: 22px; font-weight: 700; color: #fff; }
            `}</style>
        </div>
    );
}